/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import { AnyAction, Dispatch } from "@reduxjs/toolkit";

import {
    Blank,
    CreateTechnicianEntryRequest,
    DetailedUser,
    EditWorkOrderHeaderRequest,
    Effect,
    FullAssignedTechnicianEntry,
    Nullable,
    OperationSuccess,
    Pagination,
    Reference,
    Setter,
    User,
    WorkOrderDetailsResponse,
    WorkOrderStateAction,
    WorkOrderTaskEdit,
    WorkOrderTaskMessageEdit,
} from "../../utils/Interfaces";

import {
    buildDeleteRequest,
    buildGetRequest,
    buildPostRequest,
    deleteHandler,
    getHandler,
    postHandler,
    stdErrHandlerFactory,
} from "../../utils/Requests";
import { AxiosResponse } from "axios";
import { BLANK } from "../../utils/Constants";

export function getWorkOrderDetails(workOrderId: number) {
    return buildGetRequest<WorkOrderDetailsResponse>(`/api/work-orders/details/${workOrderId}`);
}

export function editWorkOrderState(workOrderId: number, action: WorkOrderStateAction) {
    return buildPostRequest<Blank, OperationSuccess>(`/api/work-orders/${action}/${workOrderId}`, {});
}

export function deleteWorkOrder(workOrderId: number, dispatch: Dispatch<AnyAction>, onWorkOrderDeleted: Effect) {
    deleteHandler<OperationSuccess>(
        `/api/work-orders/delete/${workOrderId}`,
        dispatch,
        () => {
            onWorkOrderDeleted();
        },
        stdErrHandlerFactory(dispatch)
    );
}

export function editWorkOrderHeader(
    workOrderId: number,
    ticketNo: string,
    dateReceived: Nullable<string | Date>,
    dateCreated: Nullable<string | Date>,
    deviceToBeFixed: string,
    scissorLiftAvailable: boolean,
    comments: Nullable<string>,
    dispatch: Dispatch<AnyAction>,
    onWorkOrderHeaderUpdated: Effect
) {
    // FIXME Temporary hackery to resolve time zone issues until the backend datetimes are switched to UTC.
    let dateReceivedForward = null;
    if (dateReceived !== null) {
        dateReceivedForward = new Date(typeof dateReceived === "string" ? new Date(dateReceived) : dateReceived);
        var userTimezoneOffset = dateReceivedForward.getTimezoneOffset() * 60000;
        dateReceivedForward = new Date(dateReceivedForward.getTime() - userTimezoneOffset);
        dateReceivedForward = dateReceivedForward.toISOString().replace("Z", "");
    }

    let dateCreatedForward = null;
    if (dateCreated !== null) {
        dateCreatedForward = new Date(typeof dateCreated === "string" ? new Date(dateCreated) : dateCreated);
        userTimezoneOffset = dateCreatedForward.getTimezoneOffset() * 60000;
        dateCreatedForward = new Date(dateCreatedForward.getTime() - userTimezoneOffset);
        dateCreatedForward = dateCreatedForward.toISOString().replace("Z", "");
    }

    let workOrderData: EditWorkOrderHeaderRequest = {
        date_received: dateReceivedForward,
        date_created: dateCreatedForward,
        device_to_be_fixed: deviceToBeFixed,
        ticket_no: ticketNo,
        scissor_lift_available: scissorLiftAvailable,
        comments: comments,
    };

    postHandler<EditWorkOrderHeaderRequest, Reference>(
        `/api/work-orders/update/${workOrderId}`,
        workOrderData,
        dispatch,
        () => {
            onWorkOrderHeaderUpdated();
        },
        stdErrHandlerFactory(dispatch)
    );
}

export function createTechnicianEntry(
    workOrderId: number,
    assignees: User[],
    tasks: string[],
    comments: Nullable<string>,
    dispatch: Dispatch<AnyAction>,
    onEntryCreated: Effect
) {
    postHandler<CreateTechnicianEntryRequest, Reference>(
        `/api/tech-entries/attach/${workOrderId}`,
        {
            assignees: assignees.map((user) => user.id),
            tasks: tasks,
            comments: comments,
        },
        dispatch,
        () => onEntryCreated(),
        stdErrHandlerFactory(dispatch)
    );
}

export function addTasksToTechnicianEntry(
    techEntryId: number,
    tasks: string[],
    dispatch: Dispatch<AnyAction>,
    onTaskAdded: Effect
) {
    postHandler<CreateTechnicianEntryRequest, Reference>(
        `/api/tech-entries/checklist/attach/${techEntryId}`,
        { tasks: tasks },
        dispatch,
        () => onTaskAdded(),
        stdErrHandlerFactory(dispatch)
    );
}

export function updateTechEntries(
    workOrderId: number,
    dispatch: Dispatch<AnyAction>,
    setTechEntries: Setter<FullAssignedTechnicianEntry[]>,
    setLoading: Setter<boolean>
) {
    getHandler<Pagination<FullAssignedTechnicianEntry>>(
        `/api/tech-entries/list/${workOrderId}`,
        dispatch,
        (response: AxiosResponse<Pagination<FullAssignedTechnicianEntry>, any>) => setTechEntries(response.data.results),
        stdErrHandlerFactory(dispatch),
        setLoading
    );
}

export function getAllTechnicians(dispatch: Dispatch<AnyAction>, setAllTechnicians: Setter<DetailedUser[]>) {
    getHandler<Pagination<DetailedUser>>(
        `/api/tech-entries/get-assignees`,
        dispatch,
        (response: AxiosResponse<Pagination<DetailedUser>, any>) => setAllTechnicians(response.data.results),
        stdErrHandlerFactory(dispatch)
    );
}

export function assigneesInclude(_assignee: DetailedUser, _assignees: DetailedUser[]) {
    return _assignees.filter((elem: DetailedUser) => elem.id === _assignee.id).length > 0;
}

export function createWorkOrderTask(workOrderId: number, label: string, details: string) {
    return buildPostRequest<WorkOrderTaskEdit, OperationSuccess>(`/api/work-orders/tasks/create/${workOrderId}`, {
        label: label,
        details: details,
    });
}

export function editWorkOrderTask(workOrderTaskId: number, label: string, details: string) {
    return buildPostRequest<WorkOrderTaskEdit, OperationSuccess>(`/api/work-orders/tasks/update/${workOrderTaskId}`, {
        label: label,
        details: details,
    });
}

export function deleteWorkOrderTask(workOrderTaskId: number) {
    return buildDeleteRequest<OperationSuccess>(`/api/work-orders/tasks/delete/${workOrderTaskId}`);
}

export function completeWorkOrderTask(workOrderTaskId: number) {
    return buildPostRequest<Blank, OperationSuccess>(`/api/work-orders/tasks/complete/${workOrderTaskId}`, BLANK);
}

export function incompleteWorkOrderTask(workOrderTaskId: number) {
    return buildPostRequest<Blank, OperationSuccess>(`/api/work-orders/tasks/incomplete/${workOrderTaskId}`, BLANK);
}

export function createWorkOrderTaskMessage(workOrderTaskId: number, message: string) {
    return buildPostRequest<WorkOrderTaskMessageEdit, OperationSuccess>(
        `/api/work-orders/tasks/messages/create/${workOrderTaskId}`,
        {
            message: message,
        }
    );
}

export function editWorkOrderTaskMessage(workOrderTaskMessageId: number, message: string) {
    return buildPostRequest<WorkOrderTaskMessageEdit, OperationSuccess>(
        `/api/work-orders/tasks/messages/update/${workOrderTaskMessageId}`,
        {
            message: message,
        }
    );
}

export function deleteWorkOrderTaskMessage(workOrderTaskMessageId: number) {
    return buildDeleteRequest<Blank>(`/api/work-orders/tasks/messages/delete/${workOrderTaskMessageId}`);
}
